import React from 'react';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import '@/types/table.types';
import { SLBPosition } from '@/types';
import { TruncatedCell } from './helpers/TruncatedCell';
import { TwoLineHeader } from './helpers/TwoLineHeader';
import { LentToCounterpartiesCell } from './helpers/LentToCounterpartiesCell';

const columnHelper = createColumnHelper<SLBPosition>();

// LR-589: leverage rate comes verbatim from symboldb (symbol.leverageRate) and the
// normal band is 0.12–1.00 (1.00 being the most common value). Anything above 1 is a
// rare outlier (4 rows of ~17.5k on stage) that means "question this symbol in
// symboldb", not "trade on it" — hence the same wording in the tooltip and the
// accessible name.
const LEVERAGE_ANOMALY_HINT =
  'Leverage rate above 1 is atypical — verify the symbol in symboldb';

/** The three Expected Settled Quantity columns: same cell, different horizon.
 *  Dashed unless both the figure and its base are present — a pre-LR-615 snapshot
 *  has a stored figure with no base behind it, and there is no percentage to state
 *  against nothing. */
const expectedSettledColumn = (
  id: 'quantityT1' | 'quantityT2' | 'quantityTx',
  horizon: string,
) => {
  const label = `Expected Settled Quantity ${horizon}`;
  return columnHelper.accessor(id, {
    header: () => <TwoLineHeader line1="Expected Settled" line2={`Quantity ${horizon}`} />,
    cell: (info) => {
      const value = info.getValue();
      const baseQuantity = info.row.original.settledQuantity;
      if (value == null || baseQuantity == null) {
        return <span className="text-gray-400 text-right block">-</span>;
      }
      // Magnitude, not the signed base: a negative denominator flips the sign and
      // paints a shrinking short as a fall. A zero base has no percentage at all —
      // settled 0 against a non-zero expectation is a position bought inside the
      // settlement cycle, and a grey "0.0%" there reads as "unchanged".
      const percentChange = baseQuantity ? ((value - baseQuantity) / Math.abs(baseQuantity)) * 100 : 0;
      const noPercentage = !baseQuantity && value !== 0;
      const isNegative = percentChange < 0;
      const isZero = Math.abs(percentChange) < 0.01;
      return (
        <div className="flex items-center justify-end gap-2" title={label}>
          <span className="font-medium text-gray-900">{value.toLocaleString('en-US')}</span>
          {noPercentage && <span className="text-xs min-w-[48px] text-right text-gray-400">-</span>}
          {!noPercentage && !isZero && (
            <span className={`text-xs min-w-[48px] text-right ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
              {percentChange.toFixed(1)}%
            </span>
          )}
          {!noPercentage && isZero && <span className="text-xs min-w-[48px] text-right text-gray-400">0.0%</span>}
        </div>
      );
    },
    meta: { searchable: true, headerTooltip: label },
  });
};

// TanStack ColumnDef's TValue is invariant; using `unknown` rejects heterogeneous accessor columns. `any` is the documented escape hatch.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createInventoryColumns = (_onSearchClick: (value: string) => void): ColumnDef<SLBPosition, any>[] => [
  columnHelper.display({
    id: 'rowNumber',
    header: '',
    cell: (info) => (
      <span className="text-xs text-gray-500 w-full text-center block">{info.row.index + 1}</span>
    ),
  }),
  columnHelper.accessor('legalEntity', {
    header: 'Legal Entity',
    cell: (info) => (
      <span className="font-medium text-gray-900">{info.getValue()}</span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('accountId', {
    header: 'Account ID',
    cell: (info) => (
      <span className=" text-sm text-gray-600">{info.getValue()}</span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('counterparty', {
    header: 'Counterparty',
    cell: (info) => (
      <span className="font-medium text-blue-600">{info.getValue()}</span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: (info) => (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-800">
        {info.getValue()}
      </span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('sedol', {
    header: 'SEDOL',
    cell: (info) => (
      <span className=" text-xs text-gray-500">{info.getValue()}</span>
    ),
    meta: { searchable: true, hasSearchIcon: true },
  }),
  columnHelper.accessor('isin', {
    header: 'ISIN',
    cell: (info) => (
      <span className=" text-xs text-gray-500">{info.getValue()}</span>
    ),
    meta: { searchable: true, hasSearchIcon: true },
  }),
  columnHelper.accessor('symbolId', {
    header: 'Symbol ID',
    cell: (info) => (
      <span className=" text-sm text-gray-700">{info.getValue()}</span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('exchange', {
    header: 'Exchange',
    cell: (info) => (
      <span className="text-sm text-gray-700">{info.getValue()}</span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('shortName', {
    header: 'Short Name',
    cell: (info) => {
      const value = info.getValue();
      return (
        <TruncatedCell
          content={<span className="text-gray-900">{value}</span>}
          plainText={value}
        />
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('price', {
    header: 'Price',
    cell: (info) => {
      const value = info.getValue();
      const numValue = value || 0;
      const formatted = numValue.toFixed(2);
      const parts = formatted.split('.');
      const integerPart = parseFloat(parts[0]).toLocaleString('en-US');
      return (
        <span className="font-medium text-gray-900 text-right block">
          {integerPart}.{parts[1]}
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('currency', {
    header: 'Currency',
    cell: (info) => (
      <span className=" text-sm font-medium">{info.getValue()}</span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('quantity', {
    header: () => <TwoLineHeader line1="Quantity" line2="(Trade Date)" />,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span title="Quantity (Trade Date)" className="font-medium text-gray-900 text-right block">
          {value ? value.toLocaleString('en-US') : '0'}
        </span>
      );
    },
    meta: { searchable: true, headerTooltip: 'Quantity (Trade Date)' },
  }),
  columnHelper.accessor('settledQuantity', {
    header: 'Settled Quantity',
    cell: (info) => {
      const value = info.getValue();
      return (
        <span title="Settled Quantity" className="font-medium text-gray-900 text-right block">
          {value != null ? value.toLocaleString('en-US') : '-'}
        </span>
      );
    },
    meta: { searchable: true, headerTooltip: 'Settled Quantity' },
  }),
  expectedSettledColumn('quantityT1', 'T+1'),
  expectedSettledColumn('quantityT2', 'T+2'),
  expectedSettledColumn('quantityTx', 'T+X'),
  columnHelper.accessor('pendingSettlementQuantity', {
    header: 'Pending Settlement (Due)',
    cell: (info) => {
      const value = info.getValue() ?? 0;
      return (
        <div className="flex justify-end">
          {value ? (
            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800">
              {value.toLocaleString('en-US')}
            </span>
          ) : (
            <span className="text-gray-400">0</span>
          )}
        </div>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor((row) => {
    return row.loans?.reduce((sum, loan) => sum + loan.quantityLent, 0) || 0;
  }, {
    id: 'lentToCounterparties',
    header: 'Lent to Counterparties',
    cell: (info) => {
      const row = info.row.original;
      return (
        <LentToCounterpartiesCell
          counterparty={row.counterparty}
          l2cpQuantity={row.l2cpQuantity}
          l2cpAmountEur={row.l2cpAmountEur}
          l2cpRate={row.l2cpRate}
          l2cpGrossRate={row.l2cpGrossRate}
          l2cpSnpDelta={row.l2cpSnpDelta}
          loans={row.loans}
          quantity={row.quantity}
          accountId={row.accountId}
        />
      );
    },
    meta: { searchable: true, columnGroupId: 'lentToCounterparties' },
  }),
  columnHelper.accessor('l2cpQuantity', {
    header: 'Quantity',
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className="font-medium text-gray-900 text-right block">
          {value != null ? value.toLocaleString('en-US') : '-'}
        </span>
      );
    },
    meta: { searchable: true, columnGroupId: 'lentToCounterparties' },
  }),
  columnHelper.accessor('l2cpAmountEur', {
    header: 'Amount (in EUR)',
    cell: (info) => {
      const value = info.getValue();
      const numValue = value ?? 0;
      return (
        <span className="font-medium text-gray-900 text-right block">
          {value != null ? `€${Math.round(numValue).toLocaleString('en-US')}` : '-'}
        </span>
      );
    },
    meta: { searchable: true, columnGroupId: 'lentToCounterparties' },
  }),
  columnHelper.accessor('l2cpRate', {
    header: 'Rate',
    cell: (info) => {
      const value = info.getValue();
      const n = value != null ? Number(value) : NaN;
      const display = Number.isFinite(n) ? `${n.toFixed(2)}%` : '-';
      return (
        <span className="font-medium text-gray-900 text-right block">
          {display}
        </span>
      );
    },
    meta: { searchable: true, columnGroupId: 'lentToCounterparties' },
  }),
  columnHelper.accessor('l2cpGrossRate', {
    header: 'Gross-Rate',
    cell: (info) => {
      const value = info.getValue();
      const n = value != null ? Number(value) : NaN;
      const display = Number.isFinite(n) ? `${n.toFixed(2)}%` : '-';
      return (
        <span className="font-medium text-gray-900 text-right block">
          {display}
        </span>
      );
    },
    meta: { searchable: true, columnGroupId: 'lentToCounterparties' },
  }),
  columnHelper.accessor('l2cpSnpDelta', {
    header: 'SnP-Delta',
    cell: (info) => {
      const value = info.getValue();
      const n = value != null ? Number(value) : NaN;
      const display = Number.isFinite(n) ? `${n.toFixed(2)}%` : '-';
      return (
        <span className="font-medium text-gray-900 text-right block">
          {display}
        </span>
      );
    },
    meta: { searchable: true, columnGroupId: 'lentToCounterparties' },
  }),
  columnHelper.accessor('pendingTransfers', {
    header: 'Pending Transfers',
    cell: (info) => {
      const value = info.getValue();
      return (
        <div className="flex justify-end">
          {value > 0 ? (
            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
              Out: {value.toLocaleString('en-US')}
            </span>
          ) : (
            <span className="text-sm text-gray-500">-</span>
          )}
        </div>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('leverageRate', {
    header: 'Leverage Rate',
    cell: (info) => {
      const value = info.getValue();
      // Strictly > 1: 1.00 is the most common legitimate value and must stay quiet.
      const anomalous = value > 1;
      return (
        <span
          className="font-medium text-gray-900 flex items-center justify-end gap-1"
          title={anomalous ? LEVERAGE_ANOMALY_HINT : undefined}
        >
          {anomalous && (
            // Named wrapper, hidden glyph (the MetricCell alert-dot pattern,
            // LR-542/LR-550): the heroicons svg ships aria-hidden, and a bare
            // span is `generic` which cannot carry a name — so the role and the
            // accessible name live on this span.
            <span role="img" aria-label={LEVERAGE_ANOMALY_HINT}>
              <ExclamationTriangleIcon
                aria-hidden="true"
                className="h-3.5 w-3.5 flex-none text-amber-500"
              />
            </span>
          )}
          {value}
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('valueEUR', {
    header: 'Value, EUR',
    cell: (info) => {
      const value = info.getValue();
      const numValue = value || 0;
      const rounded = Math.round(numValue);
      const formatted = rounded.toLocaleString('en-US');
      return (
        <span className="font-medium text-green-600 text-right block">
          €{formatted}
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('isLiquid', {
    header: 'Is Liquid',
    cell: (info) => {
      const isLiquid = info.getValue();
      return (
        <span
          className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
            isLiquid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {isLiquid ? 'TRUE' : 'FALSE'}
        </span>
      );
    },
  }),
  columnHelper.accessor('brokerSettlementCounterparty', {
    header: 'Primary Settlement CP',
    cell: (info) => (
      <span className="text-sm text-gray-700">{info.getValue() || '-'}</span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('brokerAccountName', {
    header: 'Primary Account Name',
    cell: (info) => (
      <span className="text-sm text-gray-700">{info.getValue() || '-'}</span>
    ),
    meta: { searchable: true },
  }),
  columnHelper.accessor('cimbEq2Quantity', {
    header: () => <TwoLineHeader line1="Quantity" line2="CIMB (EQ2)" />,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span title="CIMB (EQ2)" className="font-medium text-gray-900 text-right block">
          {value != null ? value.toLocaleString('en-US') : '-'}
        </span>
      );
    },
    meta: { searchable: true, headerTooltip: 'CIMB (EQ2)' },
  }),
  columnHelper.accessor('cimbSfQuantity', {
    header: () => <TwoLineHeader line1="Quantity" line2="CIMB (SF)" />,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span title="CIMB (SF)" className="font-medium text-gray-900 text-right block">
          {value != null ? value.toLocaleString('en-US') : '-'}
        </span>
      );
    },
    meta: { searchable: true, headerTooltip: 'CIMB (SF)' },
  }),
  columnHelper.accessor('cimbSf3Quantity', {
    header: () => <TwoLineHeader line1="Quantity" line2="CIMB (SF3)" />,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span title="CIMB (SF3)" className="font-medium text-gray-900 text-right block">
          {value != null ? value.toLocaleString('en-US') : '-'}
        </span>
      );
    },
    meta: { searchable: true, headerTooltip: 'CIMB (SF3)' },
  }),
  columnHelper.accessor('velocityLQuantity', {
    header: () => <TwoLineHeader line1="Quantity" line2="Velocity (L)" />,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span title="Velocity (L)" className="font-medium text-gray-900 text-right block">
          {value != null ? value.toLocaleString('en-US') : '-'}
        </span>
      );
    },
    meta: { searchable: true, headerTooltip: 'Velocity (L)' },
  }),
  columnHelper.accessor('velocityMQuantity', {
    header: () => <TwoLineHeader line1="Quantity" line2="Velocity (M)" />,
    cell: (info) => {
      const value = info.getValue();
      return (
        <span title="Velocity (M)" className="font-medium text-gray-900 text-right block">
          {value != null ? value.toLocaleString('en-US') : '-'}
        </span>
      );
    },
    meta: { searchable: true, headerTooltip: 'Velocity (M)' },
  }),
  columnHelper.accessor('exDate', {
    header: 'Ex-Date',
    cell: (info) => {
      const value = info.getValue();
      const displayValue = value || '-';
      return (
        <TruncatedCell
          content={<span className="text-sm text-gray-500">{displayValue}</span>}
          plainText={displayValue}
        />
      );
    },
  }),
  columnHelper.accessor('caType', {
    header: 'CA Type',
    cell: (info) => {
      const value = info.getValue() || '-';
      return (
        <TruncatedCell
          content={<span className="text-sm text-gray-500">{value}</span>}
          plainText={String(value)}
        />
      );
    },
  }),
  columnHelper.accessor('caStatus', {
    header: 'CA Status',
    cell: (info) => {
      const value = info.getValue() || '-';
      return (
        <TruncatedCell
          content={<span className="text-sm text-gray-500">{value}</span>}
          plainText={String(value)}
        />
      );
    },
  }),
  columnHelper.accessor('benchmarkFee', {
    header: 'Benchmark Fee',
    cell: (info) => {
      const value = info.getValue();
      const formatted = typeof value === 'number' ? value.toFixed(2) : '0.00';
      return (
        <span className="font-medium text-gray-900 text-right block">
          {formatted}%
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('quantityOnLoan', {
    header: 'Quantity On Loan',
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className="font-medium text-blue-600 text-right block">
          {value ? value.toLocaleString('en-US') : '0'}
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('activeUtilisation', {
    header: 'Active Utilisation',
    cell: (info) => {
      const value = info.getValue();
      const rounded = typeof value === 'number' ? Math.round(value) : 0;
      return (
        <span className="font-medium text-gray-900 text-right block">
          {rounded}%
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('currencyBenchmark', {
    header: 'Currency Benchmark',
    cell: (info) => {
      const value = info.getValue();
      const numValue = typeof value === 'number' ? value : parseFloat(String(value || '0'));
      const formatted = Number.isFinite(numValue) ? numValue.toFixed(2) : '0.00';
      return (
        <span className="text-sm text-gray-900 text-right block">
          {formatted}%
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('rebateRate', {
    header: 'Rebate Rate',
    cell: (info) => {
      const rate = info.getValue();
      const formatted = typeof rate === 'number' ? rate.toFixed(2) : '0.00';
      return (
        <span
          className={`font-medium text-right block ${
            rate >= 0 ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {formatted}%
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('dailyPotentialEUR', {
    header: 'Daily Potential, EUR',
    cell: (info) => {
      const value = info.getValue();
      const numValue = value || 0;
      const rounded = Math.round(numValue);
      const formatted = rounded.toLocaleString('en-US');
      return (
        <span className="font-medium text-green-600 text-right block">
          {formatted}
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('adtv30D', {
    header: '30D ADTV',
    cell: (info) => {
      const value = info.getValue();
      const intValue = typeof value === 'number' ? Math.round(value) : 0;
      return (
        <span className="font-medium text-gray-900 text-right block">
          {intValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('adtvMultiple', {
    header: 'ADTV Multiple',
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className="font-medium text-gray-900 text-right block">
          {typeof value === 'number' ? value.toFixed(2) : '0.00'}
        </span>
      );
    },
    meta: { searchable: true },
  }),
  columnHelper.accessor('adtvAdjustedPotentialEUR', {
    header: 'ADTV-Adjusted-Potential, EUR',
    cell: (info) => {
      const value = info.getValue();
      const numValue = value || 0;
      const rounded = Math.round(numValue);
      const formatted = rounded.toLocaleString('en-US');
      return (
        <span className="font-medium text-green-600 text-right block">
          {formatted}
        </span>
      );
    },
    meta: { searchable: true },
  }),
];

